import { useState } from "react";
import {
  HiOutlineClock,
  HiOutlineXCircle,
  HiOutlineCheckCircle,
  HiOutlineXMark,
} from "react-icons/hi2";
import { Button, ConfirmModal, Spinner, Badge, Card, showToast } from "../shared";
import { useTeamInvites, useDeleteTeamInvite } from "../../lib/react-query/teams";
import { toUsDate } from "../../utils/utils";

const ROLE_BADGE_COLOR = {
  OWNER: "warning",
  ADMIN: "primary",
  MEMBER: "neutral",
};

const StatBox = ({ label, value, borderColor }) => (
  <div className={`border rounded-lg p-3 border-l-2 bg-white ${borderColor}`}>
    <p className="text-xs text-grey">{label}</p>
    <p className="text-xl font-semibold text-darkgrey">{value}</p>
  </div>
);

const InviteRow = ({ invite, canCancel, isCancelling, onCancel, statusBadge, dimmed }) => {
  const email = invite.email || "Unknown";
  const role = invite.role || "MEMBER";
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-5 py-3.5 hover:bg-surface-hover/30 transition-colors">
      <div className={`min-w-0 ${dimmed ? "opacity-70" : ""}`}>
        <p className="text-sm font-medium text-darkgrey truncate">{email}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <Badge color={ROLE_BADGE_COLOR[role] || "neutral"} size="sm" className="uppercase tracking-wide">
            {role}
          </Badge>
          {statusBadge && (
            <Badge color={statusBadge.color} size="sm" className="uppercase tracking-wide">
              {statusBadge.label}
            </Badge>
          )}
          {invite.expires_at && (
            <span className="text-xs text-mediumgrey">
              Expires {toUsDate(invite.expires_at)}
            </span>
          )}
          {invite.accepted_at && (
            <span className="text-xs text-mediumgrey">
              Joined {toUsDate(invite.accepted_at)}
            </span>
          )}
        </div>
      </div>
      {canCancel && (
        <Button
          className="min-w-0 px-2 self-start sm:self-auto"
          variant="transparent"
          color="danger"
          size="sm"
          onClick={() => onCancel(invite.id)}
          disabled={isCancelling}
        >
          {isCancelling ? <Spinner width={14} height={14} /> : "Cancel"}
        </Button>
      )}
    </div>
  );
};

const InviteGroup = ({ title, icon, count, invites, canManageTeam, cancellingId, onCancel, statusBadge, emptyHint, dimmed, showCancel }) => {
  if (count === 0 && !emptyHint) return null;
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border bg-surface-hover/50">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-base font-semibold text-darkgrey">{title}</h2>
        </div>
        <span className="text-xs text-mediumgrey">{count} {count === 1 ? "invite" : "invites"}</span>
      </div>
      {count === 0 ? (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-darkgrey">No {title.toLowerCase()} yet.</p>
          {emptyHint && <p className="mt-1 text-xs text-mediumgrey">{emptyHint}</p>}
        </div>
      ) : (
        <div className="divide-y divide-surface-border">
          {invites.map((invite) => (
            <InviteRow
              key={invite.id}
              invite={invite}
              canCancel={canManageTeam && showCancel}
              isCancelling={cancellingId === invite.id}
              onCancel={onCancel}
              statusBadge={statusBadge}
              dimmed={dimmed}
            />
          ))}
        </div>
      )}
    </Card>
  );
};

const TeamInvitesSection = ({ teamId, canManageTeam }) => {
  const { data: invites = [], isLoading, isError } = useTeamInvites(teamId);
  const { mutateAsync: deleteInvite } = useDeleteTeamInvite();
  const [cancellingId, setCancellingId] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);

  const handleCancel = async () => {
    if (!confirmCancel) return;
    try {
      setCancellingId(confirmCancel);
      await deleteInvite({ teamId, inviteId: confirmCancel });
      showToast("Invitation cancelled", "success", { position: "top-center" });
      setConfirmCancel(null);
    } catch {
      showToast("Failed to cancel invitation", "error", { position: "top-center" });
    } finally {
      setCancellingId(null);
    }
  };

  const allInvites = Array.isArray(invites) ? invites : [];
  const pendingInvites = allInvites.filter((i) => (i.status || "PENDING") === "PENDING");
  const expiredInvites = allInvites.filter((i) => i.status === "EXPIRED");
  const acceptedInvites = allInvites.filter((i) => i.status === "ACCEPTED");
  const declinedInvites = allInvites.filter((i) => i.status === "DECLINED");
  const pendingCount = pendingInvites.length;
  const expiredCount = expiredInvites.length;
  const acceptedCount = acceptedInvites.length;
  const expiringSoonCount = pendingInvites.filter((i) => {
    if (!i.expires_at) return false;
    const days = (new Date(i.expires_at) - new Date()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 3;
  }).length;

  return (
    <div className="space-y-5">
      <Card className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatBox label="Pending invites" value={pendingCount} borderColor="border-l-secondary/60" />
          <StatBox label="Expiring soon" value={expiringSoonCount} borderColor="border-l-warning/60" />
          <StatBox label="Accepted" value={acceptedCount} borderColor="border-l-success/60" />
          <StatBox label="Expired" value={expiredCount} borderColor="border-l-danger/40" />
        </div>
      </Card>

      {isLoading ? (
        <Card className="flex items-center justify-center py-8">
          <Spinner width={24} height={24} />
        </Card>
      ) : isError ? (
        <Card className="px-5 py-4 text-sm text-danger">
          Could not load invites.
        </Card>
      ) : (
        <>
          <InviteGroup
            title="Pending invites"
            icon={<HiOutlineClock className="size-4 text-secondary-dark" />}
            count={pendingCount}
            invites={pendingInvites}
            canManageTeam={canManageTeam}
            cancellingId={cancellingId}
            onCancel={setConfirmCancel}
            emptyHint={canManageTeam ? "Invite members from the Members tab." : null}
            showCancel
          />
          {expiredInvites.length > 0 && (
            <InviteGroup
              title="Expired"
              icon={<HiOutlineXCircle className="size-4 text-danger" />}
              count={expiredCount}
              invites={expiredInvites}
              canManageTeam={canManageTeam}
              cancellingId={cancellingId}
              onCancel={setConfirmCancel}
              statusBadge={{ label: "Expired", color: "danger" }}
              dimmed
            />
          )}
          {acceptedInvites.length > 0 && (
            <InviteGroup
              title="Accepted"
              icon={<HiOutlineCheckCircle className="size-4 text-success" />}
              count={acceptedCount}
              invites={acceptedInvites}
              canManageTeam={false}
              cancellingId={cancellingId}
              onCancel={setConfirmCancel}
              statusBadge={{ label: "Accepted", color: "success" }}
            />
          )}
          {declinedInvites.length > 0 && (
            <InviteGroup
              title="Declined"
              icon={<HiOutlineXMark className="size-4 text-mediumgrey" />}
              count={declinedInvites.length}
              invites={declinedInvites}
              canManageTeam={false}
              cancellingId={cancellingId}
              onCancel={setConfirmCancel}
              statusBadge={{ label: "Declined", color: "neutral" }}
              dimmed
            />
          )}
        </>
      )}

      <ConfirmModal
        isOpen={!!confirmCancel}
        onClose={() => setConfirmCancel(null)}
        onConfirm={handleCancel}
        title="Cancel invitation"
        message="Are you sure you want to cancel this invitation? The invitee will no longer be able to join."
        confirmText="Cancel invite"
        confirmColor="danger"
        isLoading={!!cancellingId}
      />
    </div>
  );
};

export default TeamInvitesSection;
